import { useState, useEffect } from 'react';

interface PromoBannerProps {
  message: string;
  highlight?: string;
  link?: string;
  linkText?: string;
  bgColor?: string;
  textColor?: string;
  closable?: boolean;
  storageKey?: string;
}

export default function PromoBanner({
  message,
  highlight,
  link,
  linkText = 'En savoir plus',
  bgColor = 'bg-brand-500',
  textColor = 'text-white',
  closable = true,
  storageKey = 'promo_banner_closed',
}: PromoBannerProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const isClosed = sessionStorage.getItem(storageKey);
    if (isClosed) {
      setIsVisible(false);
    }
  }, [storageKey]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      if (closable) {
        sessionStorage.setItem(storageKey, '1');
      }
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`${bgColor} ${textColor} relative z-[60] transition-all duration-300 ${
        isAnimating ? 'h-0 opacity-0' : 'h-auto opacity-100'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-yellow-300 animate-pulse"></i>
            <p className="text-sm font-medium">
              {message}
              {highlight && (
                <span className="font-bold ml-1">{highlight}</span>
              )}
            </p>
          </div>
          {link && (
            <a
              href={link}
              className="inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-2 hover:no-underline transition-all whitespace-nowrap"
            >
              {linkText}
              <i className="ri-arrow-right-line text-xs"></i>
            </a>
          )}
        </div>
      </div>
      {closable && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
          aria-label="Fermer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      )}
    </div>
  );
}
